import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import LocationInformation from "../location/LocationInformation";
import CategorySelector from "../Allcategories/CategorySelector";
import { registerBusinessUser } from "../../redux/apis/registerBusinessUser";

import {
  FaUser,
  FaMapMarkerAlt,
  FaUserCircle,
  FaCalendarAlt,
  FaVenusMars,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaKey,
  FaPen,
  FaBriefcase,
  FaEyeSlash,
  FaEye,
  FaIdCard,
  FaHospital,
  FaCheckCircle,
  FaSearch,
  FaUsers,
  FaGlobe,
  FaShieldAlt,
  FaArrowRight,
  FaStethoscope,
  FaPlus,
  FaTrash,
  FaBuilding,
  FaUserPlus,
} from "react-icons/fa";

const PANEL_GRADIENT =
  "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)";

const RegistrationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ============================================================
     UI STATE
  ============================================================ */

  const [showPassword, setShowPassword] = useState(false);

  const [profilePreview, setProfilePreview] = useState("");

  const profileObjectUrlRef = useRef(null);

  const [passwordRules, setPasswordRules] = useState([
    {
      regex: /.{8,}/,
      message: "At least 8 characters",
      valid: false,
    },
    {
      regex: /[A-Z]/,
      message: "At least one uppercase letter",
      valid: false,
    },
    {
      regex: /[a-z]/,
      message: "At least one lowercase letter",
      valid: false,
    },
    {
      regex: /\d/,
      message: "At least one number",
      valid: false,
    },
  ]);

  /* ============================================================
     FORM DATA
  ============================================================ */

  const [formData, setFormData] = useState({
    fullName: "",
    dob: "",
    gender: "",
    email: "",
    phone: "",
    registration: "",
    username: "",
    password: "",
    confirmPassword: "",
    description: "",
    profession: "",
    profilePic: null,

    city: "",
    district: "",
    state: "",
    country: "",
    postalCode: "",
    googleMapLink: "",
    village: "",

    categoryPath: [],

    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    localDateTimeTS: "",
    localDateTime: "",

    socialMedia: [{ platform: "", url: "" }],

    healthSchemes: [""],
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ============================================================
     PROFILE OBJECT URL CLEANUP
  ============================================================ */

  useEffect(() => {
    return () => {
      if (profileObjectUrlRef.current) {
        URL.revokeObjectURL(profileObjectUrlRef.current);
        profileObjectUrlRef.current = null;
      }
    };
  }, []);

  /* ============================================================
     TIMEZONE
  ============================================================ */

  useEffect(() => {
    const detectedTimeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    const now = new Date();
    const timestamp = now.getTime();

    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: detectedTimeZone,
    };

    const formatter = new Intl.DateTimeFormat("en-GB", options);
    const parts = formatter.formatToParts(now);

    const getPart = (type) =>
      parts.find((part) => part.type === type)?.value || "";

    const day = getPart("day");
    const month = getPart("month");
    const year = getPart("year");
    const hour = getPart("hour");
    const minute = getPart("minute");

    const localDateTimeFormatted = `${detectedTimeZone} — ${day}/${month}/${year} ${hour}:${minute}`;

    setFormData((prev) => ({
      ...prev,
      timeZone: detectedTimeZone,
      localDateTimeTS: timestamp,
      localDateTime: localDateTimeFormatted,
    }));
  }, []);

  /* ============================================================
     GENERAL DATA CHANGE
  ============================================================ */

  const handleDataChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /* ============================================================
     PROFILE IMAGE
  ============================================================ */

  const handleProfilePicChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setErrorMessage("");

    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please select a valid image file.");
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage("Profile image must be less than 5 MB.");
      e.target.value = "";
      return;
    }

    /* Revoke previous preview URL */
    if (profileObjectUrlRef.current) {
      URL.revokeObjectURL(profileObjectUrlRef.current);
    }

    /* Create new preview URL */
    const previewUrl = URL.createObjectURL(file);

    profileObjectUrlRef.current = previewUrl;

    /* Store actual file */
    setFormData((prev) => ({
      ...prev,
      profilePic: file,
    }));

    /* Show preview */
    setProfilePreview(previewUrl);
  };

  /* ============================================================
     PASSWORD
  ============================================================ */

  const handlePasswordChange = (value) => {
    const rules = [
      {
        regex: /.{8,}/,
        message: "At least 8 characters",
      },
      {
        regex: /[A-Z]/,
        message: "At least one uppercase letter",
      },
      {
        regex: /[a-z]/,
        message: "At least one lowercase letter",
      },
      {
        regex: /\d/,
        message: "At least one number",
      },
    ];

    const updatedRules = rules.map((rule) => ({
      ...rule,
      valid: rule.regex.test(value),
    }));

    setPasswordRules(updatedRules);

    handleDataChange("password", value);
  };

  /* ============================================================
     BUILD FORM DATA
  ============================================================ */

  const buildFormData = () => {
    const data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "categoryPath") {
        value.forEach((item, index) => {
          data.append(`categoryPath[${index}]`, item);
        });
      } else if (key === "socialMedia") {
        data.append("socialMedia", JSON.stringify(value));
      } else if (key === "healthSchemes") {
        data.append("healthSchemes", JSON.stringify(value));
      } else if (key === "profilePic") {
        if (value instanceof File) {
          data.append("profilePic", value);
        }
      } else if (key === "dob") {
        data.append("dob", value || "");
      } else {
        data.append(key, value ?? "");
      }
    });

    return data;
  };

  /* ============================================================
     VALIDATION
  ============================================================ */

  const validateForm = () => {
    const {
      fullName,
      dob,
      gender,
      email,
      phone,
      username,
      password,
      confirmPassword,
      description,
      profession,
      profilePic,
      registration,
    } = formData;

    if (
      !fullName.trim() ||
      !dob ||
      !gender ||
      !email.trim() ||
      !phone ||
      !username.trim() ||
      !password ||
      !confirmPassword ||
      !description.trim() ||
      !profession.trim() ||
      !profilePic ||
      !registration.trim()
    ) {
      setErrorMessage(
        "Please complete all required registration fields."
      );

      return false;
    }

    if (phone.length !== 10) {
      setErrorMessage("Please enter a valid 10-digit phone number.");
      return false;
    }

    const allPasswordRulesValid = passwordRules.every(
      (rule) => rule.valid
    );

    if (!allPasswordRulesValid) {
      setErrorMessage(
        "Please satisfy all password requirements before continuing."
      );

      return false;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return false;
    }

    return true;
  };

  /* ============================================================
     SUBMIT
  ============================================================ */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const data = buildFormData();

      await dispatch(registerBusinessUser(data)).unwrap();

      setSuccessMessage(
        "Registration successful! Redirecting to login..."
      );

      setTimeout(() => {
        navigate("/login", {
          state: {
            success: "Registration successful! Please log in.",
          },
        });
      }, 1200);
    } catch (error) {
      console.error("Form submission error:", error);

      setErrorMessage(
        error?.message ||
          error?.payload?.message ||
          "Registration failed. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ============================================================
     DATE OF BIRTH
  ============================================================ */

  const handleDOBChange = (value) => {
    if (!value) {
      handleDataChange("dob", "");
      return;
    }

    const selectedDate = new Date(`${value}T00:00:00`);

    if (Number.isNaN(selectedDate.getTime())) {
      setErrorMessage("Invalid date selected.");
      return;
    }

    const today = new Date();

    let age =
      today.getFullYear() -
      selectedDate.getFullYear();

    const monthDiff =
      today.getMonth() -
      selectedDate.getMonth();

    const dayDiff =
      today.getDate() -
      selectedDate.getDate();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && dayDiff < 0)
    ) {
      age--;
    }

    if (age < 18) {
      setErrorMessage(
        "Healthcare provider registration requires you to be at least 18 years old."
      );

      handleDataChange("dob", "");
      return;
    }

    setErrorMessage("");

    handleDataChange("dob", value);
  };

  /* ============================================================
     HEALTH SCHEMES
  ============================================================ */

  const updateHealthScheme = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.healthSchemes];

      updated[index] = value;

      return {
        ...prev,
        healthSchemes: updated,
      };
    });
  };

  const addHealthScheme = () => {
    setFormData((prev) => ({
      ...prev,
      healthSchemes: [
        ...prev.healthSchemes,
        "",
      ],
    }));
  };

  const removeHealthScheme = (index) => {
    setFormData((prev) => ({
      ...prev,
      healthSchemes: prev.healthSchemes.filter(
        (_, i) => i !== index
      ),
    }));
  };

  /* ============================================================
     SOCIAL MEDIA
  ============================================================ */

  const updateSocialMedia = (
    index,
    key,
    value
  ) => {
    setFormData((prev) => {
      const updated = [...prev.socialMedia];

      updated[index] = {
        ...updated[index],
        [key]: value,
      };

      return {
        ...prev,
        socialMedia: updated,
      };
    });
  };

  const addSocialMedia = () => {
    setFormData((prev) => ({
      ...prev,
      socialMedia: [
        ...prev.socialMedia,
        {
          platform: "",
          url: "",
        },
      ],
    }));
  };

  const removeSocialMedia = (index) => {
    setFormData((prev) => ({
      ...prev,
      socialMedia: prev.socialMedia.filter(
        (_, i) => i !== index
      ),
    }));
  };

  /* ============================================================
     UI
  ============================================================ */

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-slate-100">

      {/* ========================================================
          BACKGROUND
      ======================================================== */}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">

        <div
          className="
            absolute
            -left-20
            -top-20
            h-56
            w-56
            rounded-full
            bg-cyan-400/20
            blur-3xl
            sm:-left-28
            sm:-top-28
            sm:h-72
            sm:w-72
            lg:h-96
            lg:w-96
          "
        />

        <div
          className="
            absolute
            -right-24
            top-1/3
            h-72
            w-72
            rounded-full
            bg-blue-500/20
            blur-3xl
            sm:-right-32
            sm:h-96
            sm:w-96
            lg:-right-40
            lg:h-[500px]
            lg:w-[500px]
          "
        />

        <div
          className="
            absolute
            bottom-0
            left-1/3
            h-64
            w-64
            rounded-full
            bg-teal-400/10
            blur-3xl
            sm:h-80
            sm:w-80
            lg:h-96
            lg:w-96
          "
        />

        <div className="absolute inset-0 opacity-[0.035]">

          <div
            className="
              absolute
              left-2
              top-16
              text-[100px]
              text-blue-700
              sm:left-8
              sm:text-[140px]
              lg:left-10
              lg:top-20
              lg:text-[180px]
            "
          >
            +
          </div>

          <div
            className="
              absolute
              bottom-5
              right-5
              text-[130px]
              text-blue-700
              sm:right-10
              sm:text-[170px]
              lg:right-20
              lg:bottom-10
              lg:text-[220px]
            "
          >
            +
          </div>

        </div>

      </div>

      {/* ========================================================
          SUBMITTING MODAL
      ======================================================== */}

      {isSubmitting && (
        <div
          className="
            fixed
            inset-0
            z-[100]
            flex
            items-center
            justify-center
            bg-slate-950/60
            p-4
            backdrop-blur-sm
          "
        >
          <div
            className="
              w-full
              max-w-sm
              rounded-2xl
              bg-white
              p-6
              text-center
              shadow-2xl
              sm:p-8
            "
          >

            <div
              className="
                mx-auto
                mb-5
                flex
                h-14
                w-14
                items-center
                justify-center
                rounded-full
                bg-blue-50
              "
            >
              <svg
                className="h-7 w-7 animate-spin text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />

                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
            </div>

            <h3 className="text-lg font-bold text-slate-800">
              Creating your profile
            </h3>

            <p className="mt-2 text-sm leading-5 text-slate-500">
              Please wait while we securely process your registration.
            </p>

          </div>
        </div>
      )}

      {/* ========================================================
          ERROR MODAL
      ======================================================== */}

      {errorMessage && (
        <div
          className="
            fixed
            inset-0
            z-[110]
            flex
            items-center
            justify-center
            bg-slate-950/60
            p-4
            backdrop-blur-sm
          "
        >
          <div
            className="
              w-full
              max-w-md
              overflow-hidden
              rounded-2xl
              bg-white
              shadow-2xl
            "
          >

            <div
              className="
                border-b
                border-red-100
                bg-red-50
                px-4
                py-4
                sm:px-6
                sm:py-5
              "
            >
              <div className="flex items-start gap-3">

                <div
                  className="
                    flex
                    h-10
                    w-10
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-red-100
                    text-red-600
                  "
                >
                  !
                </div>

                <div className="min-w-0">

                  <h3 className="font-bold text-red-700">
                    Registration Error
                  </h3>

                  <p className="mt-0.5 text-xs text-red-500">
                    Please check the information below.
                  </p>

                </div>

              </div>
            </div>

            <div className="p-4 sm:p-6">

              <p className="break-words text-sm leading-6 text-slate-600">
                {errorMessage}
              </p>

              <button
                type="button"
                onClick={() => setErrorMessage("")}
                className="
                  mt-5
                  w-full
                  rounded-xl
                  bg-red-600
                  py-3
                  font-semibold
                  text-white
                  transition
                  hover:bg-red-700
                  sm:mt-6
                "
              >
                Close
              </button>

            </div>

          </div>
        </div>
      )}

      {/* ========================================================
          MAIN RESPONSIVE SHELL
      ======================================================== */}

      <div
        className="
          relative
          z-10
          min-h-screen
          w-full
          px-2
          py-2
          sm:px-4
          sm:py-4
          md:px-5
          md:py-5
          lg:px-8
          lg:py-8
        "
      >

        <div
          className="
            mx-auto
            flex
            min-h-[calc(100vh-16px)]
            w-full
            max-w-[1600px]
            flex-col
            overflow-hidden
            rounded-xl
            border
            border-white/70
            bg-white/80
            shadow-2xl
            backdrop-blur-xl
            sm:min-h-[calc(100vh-32px)]
            sm:rounded-2xl
            md:min-h-[calc(100vh-40px)]
            lg:min-h-[calc(100vh-64px)]
            lg:flex-row
            lg:rounded-3xl
          "
        >

          {/* ======================================================
              LEFT BENEFITS PANEL
          ====================================================== */}

          <aside
            className="
              relative
              hidden
              overflow-hidden
              text-white
              lg:flex
              lg:w-[36%]
              xl:w-[38%]
              2xl:w-[40%]
            "
            style={{
              background: PANEL_GRADIENT,
            }}
          >

            <div
              className="
                absolute
                -right-32
                -top-32
                h-80
                w-80
                rounded-full
                border
                border-white/10
                xl:h-96
                xl:w-96
              "
            />

            <div
              className="
                absolute
                -bottom-40
                -left-32
                h-[420px]
                w-[420px]
                rounded-full
                border
                border-white/10
                xl:h-[500px]
                xl:w-[500px]
              "
            />

            <div
              className="
                absolute
                right-8
                top-1/2
                h-20
                w-20
                rounded-full
                bg-white/5
                backdrop-blur-sm
              "
            />

            <div
              className="
                absolute
                -left-10
                top-1/4
                h-32
                w-32
                rounded-full
                bg-white/5
              "
            />

            <div
              className="
                relative
                z-10
                flex
                h-full
                min-h-0
                w-full
                flex-col
                p-7
                xl:p-9
                2xl:p-10
              "
            >

              {/* Logo */}

              <button
                type="button"
                onClick={() => navigate("/Home")}
                className="
                  flex
                  w-fit
                  shrink-0
                  items-center
                  rounded-lg
                  focus:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-cyan-300
                "
              >
                <img
                  src="/logo.jpeg"
                  alt="MR Health"
                  title="MR Health"
                  className="
                    h-11
                    w-auto
                    max-w-[180px]
                    object-contain
                    xl:h-13
                    2xl:h-14
                  "
                />
              </button>

              {/* Badge */}

              <div
                className="
                  mt-6
                  inline-flex
                  w-fit
                  max-w-full
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-white/20
                  bg-white/10
                  px-3
                  py-1.5
                  text-[11px]
                  font-semibold
                  backdrop-blur-sm
                  xl:mt-7
                  xl:text-xs
                "
              >
                <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-300" />

                <span className="truncate">
                  Healthcare Provider Registration
                </span>
              </div>

              {/* Icon */}

              <div
                className="
                  mt-5
                  flex
                  h-12
                  w-12
                  shrink-0
                  items-center
                  justify-center
                  rounded-2xl
                  bg-white/15
                  backdrop-blur-sm
                  xl:h-14
                  xl:w-14
                "
              >
                <FaUserPlus className="text-xl xl:text-2xl" />
              </div>

              {/* Heading */}

              <h1
                className="
                  mt-4
                  max-w-xl
                  break-words
                  text-2xl
                  font-bold
                  leading-tight
                  tracking-tight
                  xl:mt-5
                  xl:text-3xl
                  2xl:text-4xl
                "
              >
                Grow your healthcare presence with{" "}
                <span className="text-cyan-200">
                  MRHealth.
                </span>
              </h1>

              {/* Description */}

              <p
                className="
                  mt-3
                  max-w-xl
                  text-sm
                  leading-relaxed
                  text-sky-100
                  xl:mt-4
                  xl:text-base
                "
              >
                Register your healthcare profile and make
                your services easier to discover by patients
                looking for healthcare near them.
              </p>

              {/* Benefits */}

              <div
                className="
                  mt-5
                  space-y-3
                  xl:mt-7
                  xl:space-y-4
                "
              >

                <BenefitItem
                  icon={<FaSearch />}
                  title="Get discovered"
                  text="Help patients find your healthcare service easily."
                />

                <BenefitItem
                  icon={<FaUsers />}
                  title="Reach more patients"
                  text="Present your professional services in one trusted place."
                />

                <BenefitItem
                  icon={<FaBuilding />}
                  title="Show your services"
                  text="Add specialties, categories and healthcare information."
                />

                <BenefitItem
                  icon={<FaMapMarkerAlt />}
                  title="Be found locally"
                  text="Help patients discover your healthcare location and service area."
                />

                <BenefitItem
                  icon={<FaShieldAlt />}
                  title="Build trust"
                  text="Provide professional and registration details to patients."
                />

              </div>

              {/* Platform */}

              <div
                className="
                  mt-auto
                  rounded-2xl
                  border
                  border-white/20
                  bg-white/10
                  p-3
                  backdrop-blur-sm
                  xl:p-4
                "
              >
                <p className="text-[11px] text-sky-100 xl:text-xs">
                  mrhealth.co.in is designed for
                </p>

                <div
                  className="
                    mt-2
                    flex
                    flex-wrap
                    gap-2
                    xl:mt-3
                    xl:gap-3
                  "
                >

                  <span
                    className="
                      rounded-full
                      bg-white/15
                      px-2.5
                      py-1
                      text-[11px]
                      xl:px-3
                      xl:py-1.5
                      xl:text-sm
                    "
                  >
                    👨‍⚕️ Doctors
                  </span>

                  <span
                    className="
                      rounded-full
                      bg-white/15
                      px-2.5
                      py-1
                      text-[11px]
                      xl:px-3
                      xl:py-1.5
                      xl:text-sm
                    "
                  >
                    🧑‍⚕️ Patients
                  </span>

                  <span
                    className="
                      rounded-full
                      bg-white/15
                      px-2.5
                      py-1
                      text-[11px]
                      xl:px-3
                      xl:py-1.5
                      xl:text-sm
                    "
                  >
                    🏥 Healthcare Services
                  </span>

                </div>
              </div>

            </div>
          </aside>

          {/* ======================================================
              RIGHT FORM PANEL
          ====================================================== */}

          <main
            className="
              flex
              min-h-0
              min-w-0
              flex-1
              flex-col
              bg-white
            "
          >

            {/* Header */}

            <div
              className="
                shrink-0
                border-b
                border-slate-200
                bg-white/95
                px-4
                py-4
                backdrop-blur
                sm:px-6
                sm:py-5
                md:px-7
                lg:px-8
                xl:px-9
              "
            >

              <div
                className="
                  flex
                  min-w-0
                  items-start
                  justify-between
                  gap-3
                  sm:items-center
                  sm:gap-4
                "
              >

                <div className="min-w-0 flex-1">

                  <div
                    className="
                      flex
                      items-center
                      gap-2
                      text-[10px]
                      font-bold
                      uppercase
                      tracking-wider
                      text-blue-600
                      sm:text-xs
                    "
                  >
                    <FaStethoscope className="shrink-0" />
                    <span>MRHealth</span>
                  </div>

                  <h2
                    className="
                      mt-1
                      break-words
                      text-lg
                      font-bold
                      leading-tight
                      text-slate-900
                      sm:text-2xl
                    "
                  >
                    Healthcare Provider Registration
                  </h2>

                  <p
                    className="
                      mt-1
                      max-w-2xl
                      text-[11px]
                      leading-5
                      text-slate-500
                      sm:text-sm
                    "
                  >
                    Create your professional healthcare profile.
                  </p>

                </div>

                <div
                  className="
                    hidden
                    shrink-0
                    items-center
                    gap-2
                    rounded-xl
                    border
                    border-emerald-100
                    bg-emerald-50
                    px-3
                    py-2
                    sm:flex
                  "
                >
                  <FaCheckCircle className="text-emerald-500" />

                  <span className="text-xs font-semibold text-emerald-700">
                    1 Month Free
                  </span>
                </div>

              </div>
            </div>

            {/* Scrollable form */}

            <form
              onSubmit={handleSubmit}
              className="
                min-h-0
                flex-1
                overflow-y-auto
                overflow-x-hidden
                custom-scrollbar
              "
            >

              <div
                className="
                  mx-auto
                  w-full
                  max-w-5xl
                  px-3
                  py-4
                  sm:px-5
                  sm:py-6
                  md:px-6
                  lg:px-8
                  lg:py-7
                  xl:px-10
                  2xl:py-8
                "
              >

                {/* ==================================================
                    MOBILE / TABLET BENEFITS
                ================================================== */}

                <div
                  className="
                    mb-5
                    rounded-2xl
                    bg-gradient-to-br
                    from-blue-700
                    to-cyan-600
                    p-4
                    text-white
                    sm:p-5
                    lg:hidden
                  "
                >

                  <div className="flex items-start gap-3">

                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-white
                        sm:h-11
                        sm:w-11
                      "
                    >
                      <FaStethoscope className="text-blue-600" />
                    </div>

                    <div className="min-w-0">

                      <h3 className="text-base font-bold sm:text-lg">
                        Join MRHealth
                      </h3>

                      <p className="mt-0.5 text-xs leading-5 text-blue-100">
                        Make your healthcare service easier to find.
                      </p>

                    </div>

                  </div>

                  <div
                    className="
                      mt-4
                      grid
                      grid-cols-1
                      gap-2
                      min-[400px]:grid-cols-2
                    "
                  >
                    <MiniBenefit text="Get discovered" />
                    <MiniBenefit text="Reach patients" />
                    <MiniBenefit text="Show services" />
                    <MiniBenefit text="Build trust" />
                  </div>

                </div>

                {/* ==================================================
                    FREE TRIAL
                ================================================== */}

                <div
                  className="
                    mb-5
                    rounded-2xl
                    border
                    border-emerald-200
                    bg-gradient-to-r
                    from-emerald-50
                    to-cyan-50
                    p-3
                    sm:p-4
                  "
                >

                  <div className="flex items-start gap-3">

                    <div
                      className="
                        flex
                        h-9
                        w-9
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-emerald-100
                        sm:h-10
                        sm:w-10
                      "
                    >
                      <FaCheckCircle className="text-emerald-600" />
                    </div>

                    <div className="min-w-0">

                      <h3 className="text-sm font-bold text-emerald-800 sm:text-base">
                        Start with 1 Month Free
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-emerald-700 sm:text-sm">
                        Register your healthcare profile and explore
                        MRHealth with no payment required for the first month.
                      </p>

                    </div>

                  </div>

                </div>

                {/* ==================================================
                    01 PROFILE
                ================================================== */}

                <SectionCard
                  number="01"
                  icon={<FaUser />}
                  title="Profile"
                  subtitle="Add your professional profile image."
                >

                  <div className="flex flex-col items-center py-1 sm:py-2">

                    <label
                      htmlFor="profilePic"
                      className="group cursor-pointer"
                    >

                      <div
                        className="
                          relative
                          h-24
                          w-24
                          sm:h-28
                          sm:w-28
                          md:h-32
                          md:w-32
                        "
                      >

                        {/* Profile Image / Default React Icon */}

                        <div
                          className="
                            flex
                            h-full
                            w-full
                            items-center
                            justify-center
                            overflow-hidden
                            rounded-full
                            border-4
                            border-white
                            bg-slate-100
                            shadow-xl
                            ring-2
                            ring-blue-100
                          "
                        >

                          {profilePreview ? (
                            <img
                              src={profilePreview}
                              alt="Profile"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <FaUserCircle
                              className="
                                h-full
                                w-full
                                text-slate-300
                              "
                            />
                          )}

                        </div>

                        {/* Hover Overlay */}

                        <div
                          className="
                            absolute
                            inset-0
                            flex
                            items-center
                            justify-center
                            rounded-full
                            bg-slate-950/50
                            text-sm
                            font-semibold
                            text-white
                            opacity-0
                            transition
                            group-hover:opacity-100
                          "
                        >
                          Change
                        </div>

                        {/* Plus Button */}

                        <div
                          className="
                            absolute
                            bottom-0
                            right-0
                            flex
                            h-8
                            w-8
                            items-center
                            justify-center
                            rounded-full
                            border-4
                            border-white
                            bg-blue-600
                            text-white
                            sm:h-9
                            sm:w-9
                          "
                        >
                          <FaPlus className="text-[10px]" />
                        </div>

                      </div>

                      <input
                        type="file"
                        id="profilePic"
                        name="profilePic"
                        onChange={handleProfilePicChange}
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                      />

                    </label>

                    <p className="mt-3 text-center text-sm font-semibold text-slate-700">
                      Upload Profile Image
                    </p>

                    <p className="mt-1 text-center text-[11px] leading-4 text-slate-400 sm:text-xs">
                      JPG, PNG, WEBP or GIF • Maximum 5 MB
                    </p>

                  </div>

                </SectionCard>

                {/* ==================================================
                    02 PERSONAL INFORMATION
                ================================================== */}

                <SectionCard
                  number="02"
                  icon={<FaUser />}
                  title="Personal Information"
                  subtitle="Enter your basic professional information."
                >

                  <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">

                    <FormField
                      label="Full Name"
                      icon={<FaUser />}
                      required
                    >
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) =>
                          handleDataChange(
                            "fullName",
                            e.target.value
                          )
                        }
                        className="input-style"
                        placeholder="Enter full name"
                        required
                      />
                    </FormField>

                    <FormField
                      label="Date of Birth"
                      icon={<FaCalendarAlt />}
                      required
                    >
                      <input
                        type="date"
                        value={formData.dob}
                        onChange={(e) =>
                          handleDOBChange(e.target.value)
                        }
                        className="input-style"
                        required
                      />
                    </FormField>

                    <FormField
                      label="Gender"
                      icon={<FaVenusMars />}
                      required
                    >
                      <select
                        value={formData.gender}
                        onChange={(e) =>
                          handleDataChange(
                            "gender",
                            e.target.value
                          )
                        }
                        className="input-style"
                        required
                      >
                        <option value="">
                          Select Gender
                        </option>

                        <option value="Male">
                          Male
                        </option>

                        <option value="Female">
                          Female
                        </option>

                        <option value="Other">
                          Other
                        </option>
                      </select>
                    </FormField>

                    <FormField
                      label="Phone Number"
                      icon={<FaPhone />}
                      required
                    >
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (/^\d{0,10}$/.test(value)) {
                            handleDataChange(
                              "phone",
                              value
                            );
                          }
                        }}
                        className="input-style"
                        placeholder="10-digit mobile number"
                        required
                      />

                      {formData.phone &&
                        formData.phone.length !== 10 && (
                          <p className="mt-1 text-xs text-red-500">
                            Enter exactly 10 digits.
                          </p>
                        )}
                    </FormField>

                    <FormField
                      label="Email Address"
                      icon={<FaEnvelope />}
                      required
                      full
                    >
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleDataChange(
                            "email",
                            e.target.value
                          )
                        }
                        className="input-style"
                        placeholder="Enter professional email"
                        required
                      />
                    </FormField>

                  </div>

                </SectionCard>

                {/* ==================================================
                    03 ACCOUNT
                ================================================== */}

                <SectionCard
                  number="03"
                  icon={<FaLock />}
                  title="Account Information"
                  subtitle="Create secure login credentials."
                >

                  <div className="space-y-4">

                    <FormField
                      label="Username"
                      icon={<FaUser />}
                      required
                    >
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) =>
                          handleDataChange(
                            "username",
                            e.target.value
                          )
                        }
                        className="input-style"
                        placeholder="Create username"
                        required
                      />
                    </FormField>

                    <FormField
                      label="Password"
                      icon={<FaLock />}
                      required
                    >

                      <div className="relative min-w-0">

                        <input
                          type={
                            showPassword
                              ? "text"
                              : "password"
                          }
                          value={formData.password}
                          onChange={(e) =>
                            handlePasswordChange(
                              e.target.value
                            )
                          }
                          className="input-style pr-12"
                          placeholder="Create secure password"
                          required
                        />

                        <button
                          type="button"
                          onClick={() =>
                            setShowPassword(
                              (prev) => !prev
                            )
                          }
                          aria-label={
                            showPassword
                              ? "Hide password"
                              : "Show password"
                          }
                          className="
                            absolute
                            right-3
                            top-1/2
                            flex
                            h-8
                            w-8
                            -translate-y-1/2
                            items-center
                            justify-center
                            rounded-lg
                            text-slate-400
                            transition
                            hover:bg-slate-100
                            hover:text-blue-600
                          "
                        >
                          {showPassword ? (
                            <FaEyeSlash />
                          ) : (
                            <FaEye />
                          )}
                        </button>

                      </div>

                      {formData.password && (
                        <div
                          className="
                            mt-3
                            grid
                            grid-cols-1
                            gap-1.5
                            min-[420px]:grid-cols-2
                          "
                        >
                          {passwordRules.map(
                            (rule, index) => (
                              <div
                                key={index}
                                className={`
                                  flex
                                  min-w-0
                                  items-start
                                  gap-1
                                  text-xs
                                  ${
                                    rule.valid
                                      ? "text-emerald-600"
                                      : "text-red-500"
                                  }
                                `}
                              >

                                <span className="mt-0.5 shrink-0">
                                  {rule.valid ? (
                                    <FaCheckCircle />
                                  ) : (
                                    "●"
                                  )}
                                </span>

                                <span className="break-words">
                                  {rule.message}
                                </span>

                              </div>
                            )
                          )}
                        </div>
                      )}

                    </FormField>

                    <FormField
                      label="Confirm Password"
                      icon={<FaKey />}
                      required
                    >
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) =>
                          handleDataChange(
                            "confirmPassword",
                            e.target.value
                          )
                        }
                        className="input-style"
                        placeholder="Confirm password"
                        required
                      />

                      {formData.confirmPassword &&
                        formData.confirmPassword !==
                          formData.password && (
                          <p className="mt-1 text-xs text-red-500">
                            Passwords do not match.
                          </p>
                        )}
                    </FormField>

                  </div>

                </SectionCard>

                {/* ==================================================
                    04 HEALTHCARE INFORMATION
                ================================================== */}

                <SectionCard
                  number="04"
                  icon={<FaHospital />}
                  title="Healthcare Information"
                  subtitle="Tell patients about your healthcare service."
                >

                  <div className="space-y-5">

                    <FormField
                      label="Healthcare Profession / Service"
                      icon={<FaBriefcase />}
                      required
                    >
                      <input
                        type="text"
                        value={formData.profession}
                        onChange={(e) =>
                          handleDataChange(
                            "profession",
                            e.target.value
                          )
                        }
                        className="input-style"
                        placeholder="Doctor, Hospital, Clinic, Laboratory, Pharmacy, etc."
                        required
                      />
                    </FormField>

                    <FormField
                      label="Registration Number"
                      icon={<FaIdCard />}
                      required
                    >
                      <input
                        type="text"
                        value={formData.registration}
                        onChange={(e) =>
                          handleDataChange(
                            "registration",
                            e.target.value.trimStart()
                          )
                        }
                        className="input-style"
                        placeholder="Enter professional registration number"
                        required
                      />
                    </FormField>

                    <FormField
                      label="Healthcare Service Description"
                      icon={<FaPen />}
                      required
                    >
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          handleDataChange(
                            "description",
                            e.target.value
                          )
                        }
                        rows={5}
                        className="input-style resize-none"
                        placeholder="Describe your healthcare service, opening hours, working days, services offered and display name..."
                        required
                      />

                      <p className="mt-1 text-xs leading-5 text-slate-400">
                        Include opening hours, working days,
                        services and your preferred display name.
                      </p>
                    </FormField>

                  </div>

                </SectionCard>

                {/* ==================================================
                    05 HEALTH SCHEMES
                ================================================== */}

                <SectionCard
                  number="05"
                  icon={<FaShieldAlt />}
                  title="Health Insurance & Support Schemes"
                  subtitle="Add accepted health schemes or insurance programs."
                >

                  <div className="space-y-3">

                    {formData.healthSchemes.map(
                      (scheme, index) => (
                        <div
                          key={index}
                          className="flex min-w-0 items-start gap-2"
                        >

                          <input
                            type="text"
                            value={scheme}
                            onChange={(e) =>
                              updateHealthScheme(
                                index,
                                e.target.value
                              )
                            }
                            className="input-style min-w-0 flex-1"
                            placeholder="Example: Aarogyasri, Ayushman Bharat, Insurance"
                          />

                          {formData.healthSchemes.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                removeHealthScheme(index)
                              }
                              aria-label="Remove health scheme"
                              className="
                                flex
                                h-11
                                w-11
                                shrink-0
                                items-center
                                justify-center
                                rounded-xl
                                bg-red-50
                                text-red-500
                                transition
                                hover:bg-red-100
                              "
                            >
                              <FaTrash />
                            </button>
                          )}

                        </div>
                      )
                    )}

                    <button
                      type="button"
                      onClick={addHealthScheme}
                      className="
                        inline-flex
                        max-w-full
                        items-center
                        gap-2
                        rounded-xl
                        bg-blue-50
                        px-3
                        py-2.5
                        text-sm
                        font-semibold
                        text-blue-700
                        transition
                        hover:bg-blue-100
                        sm:px-4
                      "
                    >
                      <FaPlus className="shrink-0" />
                      <span>Add Another Scheme</span>
                    </button>

                  </div>

                </SectionCard>

                {/* ==================================================
                    06 CATEGORY
                ================================================== */}

                <SectionCard
                  number="06"
                  icon={<FaStethoscope />}
                  title="Healthcare Categories"
                  subtitle="Select the healthcare categories and specialties that describe your service."
                >

                  <div className="min-w-0">

                    <CategorySelector
                      onCategoryDataChange={(
                        key,
                        value
                      ) =>
                        handleDataChange(
                          key,
                          value
                        )
                      }
                    />

                  </div>

                </SectionCard>

                {/* ==================================================
                    07 SOCIAL MEDIA
                ================================================== */}

                <SectionCard
                  number="07"
                  icon={<FaGlobe />}
                  title="Online Presence"
                  subtitle="Add your website and social media profiles."
                >

                  <div className="space-y-4">

                    {formData.socialMedia.map(
                      (item, index) => (
                        <div
                          key={index}
                          className="
                            min-w-0
                            rounded-xl
                            border
                            border-slate-200
                            bg-slate-50
                            p-3
                            sm:p-4
                          "
                        >

                          <div
                            className="
                              grid
                              min-w-0
                              grid-cols-1
                              gap-3
                              md:grid-cols-2
                            "
                          >

                            <div className="min-w-0">

                              <label className="field-label">
                                Platform
                              </label>

                              <select
                                value={item.platform}
                                onChange={(e) =>
                                  updateSocialMedia(
                                    index,
                                    "platform",
                                    e.target.value
                                  )
                                }
                                className="input-style"
                              >

                                <option value="">
                                  Select Platform
                                </option>

                                <option value="Instagram">
                                  Instagram
                                </option>

                                <option value="Facebook">
                                  Facebook
                                </option>

                                <option value="Twitter">
                                  Twitter (X)
                                </option>

                                <option value="LinkedIn">
                                  LinkedIn
                                </option>

                                <option value="YouTube">
                                  YouTube
                                </option>

                                <option value="Website">
                                  Official Website
                                </option>

                                <option value="Google Business">
                                  Google Business
                                </option>

                                <option value="WhatsApp">
                                  WhatsApp
                                </option>

                                <option value="Telegram">
                                  Telegram
                                </option>

                                <option value="Other">
                                  Other
                                </option>

                              </select>

                            </div>

                            <div className="min-w-0">

                              <label className="field-label">
                                Profile / Website URL
                              </label>

                              <input
                                type="url"
                                value={item.url}
                                onChange={(e) =>
                                  updateSocialMedia(
                                    index,
                                    "url",
                                    e.target.value
                                  )
                                }
                                className="input-style"
                                placeholder="https://..."
                              />

                            </div>

                          </div>

                          {formData.socialMedia.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                removeSocialMedia(index)
                              }
                              className="
                                mt-3
                                inline-flex
                                items-center
                                gap-1
                                text-xs
                                font-semibold
                                text-red-500
                                transition
                                hover:text-red-700
                              "
                            >
                              <FaTrash />
                              Remove
                            </button>
                          )}

                        </div>
                      )
                    )}

                    <button
                      type="button"
                      onClick={addSocialMedia}
                      className="
                        inline-flex
                        max-w-full
                        items-center
                        gap-2
                        rounded-xl
                        bg-blue-50
                        px-3
                        py-2.5
                        text-sm
                        font-semibold
                        text-blue-700
                        transition
                        hover:bg-blue-100
                        sm:px-4
                      "
                    >
                      <FaPlus className="shrink-0" />
                      <span>Add Another Link</span>
                    </button>

                  </div>

                </SectionCard>

                {/* ==================================================
                    08 LOCATION
                ================================================== */}

                <SectionCard
                  number="08"
                  icon={<FaMapMarkerAlt />}
                  title="Location Information"
                  subtitle="Help patients find your healthcare service."
                >

                  <div className="min-w-0">

                    <LocationInformation
                      formData={formData}
                      onLocationDataChange={(
                        key,
                        value
                      ) =>
                        handleDataChange(
                          key,
                          value
                        )
                      }
                    />

                  </div>

                </SectionCard>

                {/* ==================================================
                    SUCCESS
                ================================================== */}

                {successMessage && (
                  <div
                    className="
                      mb-5
                      flex
                      items-start
                      gap-3
                      rounded-xl
                      border
                      border-emerald-200
                      bg-emerald-50
                      p-3
                      sm:p-4
                    "
                  >

                    <FaCheckCircle className="mt-0.5 shrink-0 text-emerald-600" />

                    <p className="break-words text-sm font-medium leading-5 text-emerald-700">
                      {successMessage}
                    </p>

                  </div>
                )}

                {/* ==================================================
                    SUBMIT
                ================================================== */}

                <div className="pb-5 pt-1 sm:pb-8">

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="
                      flex
                      w-full
                      items-center
                      justify-center
                      gap-2
                      rounded-2xl
                      bg-gradient-to-r
                      from-blue-600
                      to-cyan-600
                      px-4
                      py-3.5
                      text-sm
                      font-bold
                      text-white
                      shadow-lg
                      shadow-blue-500/20
                      transition-all
                      hover:from-blue-700
                      hover:to-cyan-700
                      disabled:cursor-not-allowed
                      disabled:opacity-60
                      sm:gap-3
                      sm:py-4
                      sm:text-base
                    "
                  >

                    {isSubmitting ? (
                      <>
                        <svg
                          className="h-5 w-5 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />

                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>

                        Creating Profile...
                      </>
                    ) : (
                      <>
                        <span className="truncate">
                          Create Healthcare Profile
                        </span>

                        <FaArrowRight className="shrink-0" />
                      </>
                    )}

                  </button>

                  <p
                    className="
                      mx-auto
                      mt-3
                      max-w-2xl
                      px-2
                      text-center
                      text-[10px]
                      leading-4
                      text-slate-400
                      sm:text-xs
                    "
                  >
                    By registering, you agree to provide accurate
                    healthcare and professional information.
                  </p>

                </div>

              </div>
            </form>

          </main>
        </div>
      </div>

      {/* ========================================================
          RESPONSIVE STYLES
      ======================================================== */}

      <style>{`
        *,
        *::before,
        *::after {
          box-sizing: border-box;
        }

        html,
        body {
          max-width: 100%;
          overflow-x: hidden;
        }

        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 transparent;
          -webkit-overflow-scrolling: touch;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 7px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 999px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .input-style {
          display: block;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          min-height: 44px;
          padding: 10px 13px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #ffffff;
          color: #0f172a;
          outline: none;
          transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease,
            background-color 0.2s ease;
          font-size: 14px;
          line-height: 1.4;
          box-sizing: border-box;
        }

        .input-style::placeholder {
          color: #94a3b8;
        }

        .input-style:focus {
          border-color: #38bdf8;
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.10);
        }

        .input-style:disabled {
          cursor: not-allowed;
          background: #f8fafc;
        }

        textarea.input-style {
          min-height: 120px;
        }

        .field-label {
          display: block;
          margin-bottom: 6px;
          color: #334155;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.35;
        }

        @media (max-width: 639px) {
          .input-style {
            min-height: 43px;
            padding: 9px 12px;
            font-size: 14px;
            border-radius: 11px;
          }

          textarea.input-style {
            min-height: 115px;
          }

          .field-label {
            font-size: 12px;
          }
        }

        @media (max-width: 359px) {
          .input-style {
            font-size: 13px;
          }

          .field-label {
            font-size: 11.5px;
          }
        }

        @media (min-width: 1536px) {
          .input-style {
            min-height: 46px;
          }
        }
      `}</style>
    </div>
  );
};

/* ============================================================
   BENEFIT ITEM
============================================================ */

const BenefitItem = ({
  icon,
  title,
  text,
}) => {
  return (
    <div className="flex min-w-0 items-start gap-3 xl:gap-4">

      <div
        className="
          flex
          h-9
          w-9
          shrink-0
          items-center
          justify-center
          rounded-xl
          border
          border-white/15
          bg-white/10
          text-cyan-100
          xl:h-10
          xl:w-10
        "
      >
        {icon}
      </div>

      <div className="min-w-0">

        <h3 className="break-words text-sm font-bold">
          {title}
        </h3>

        <p
          className="
            mt-1
            break-words
            text-[11px]
            leading-4
            text-blue-100
            xl:text-xs
            xl:leading-5
          "
        >
          {text}
        </p>

      </div>

    </div>
  );
};

/* ============================================================
   MINI BENEFIT
============================================================ */

const MiniBenefit = ({ text }) => {
  return (
    <div className="flex min-w-0 items-center gap-2 text-xs font-medium">

      <FaCheckCircle className="shrink-0 text-cyan-200" />

      <span className="truncate">
        {text}
      </span>

    </div>
  );
};

/* ============================================================
   SECTION CARD
============================================================ */

const SectionCard = ({
  number,
  icon,
  title,
  subtitle,
  children,
}) => {
  return (
    <section
      className="
        mb-4
        min-w-0
        overflow-hidden
        rounded-2xl
        border
        border-slate-200
        bg-white
        shadow-sm
        sm:mb-5
      "
    >

      {/* Header */}

      <div
        className="
          border-b
          border-slate-100
          bg-slate-50/70
          px-3
          py-3.5
          sm:px-5
          sm:py-4
        "
      >

        <div className="flex min-w-0 items-start gap-2.5 sm:gap-3">

          <div
            className="
              flex
              h-8
              w-8
              shrink-0
              items-center
              justify-center
              rounded-xl
              bg-blue-50
              text-blue-600
              sm:h-9
              sm:w-9
            "
          >
            {icon}
          </div>

          <div className="min-w-0 flex-1">

            <div className="flex min-w-0 items-start gap-1.5 sm:items-center sm:gap-2">

              <span
                className="
                  shrink-0
                  pt-0.5
                  text-[9px]
                  font-bold
                  tracking-wider
                  text-blue-500
                  sm:text-[10px]
                "
              >
                {number}
              </span>

              <h3
                className="
                  min-w-0
                  break-words
                  text-sm
                  font-bold
                  leading-5
                  text-slate-800
                  sm:text-base
                "
              >
                {title}
              </h3>

            </div>

            <p
              className="
                mt-0.5
                break-words
                text-[10px]
                leading-4
                text-slate-500
                sm:text-xs
                sm:leading-5
              "
            >
              {subtitle}
            </p>

          </div>

        </div>

      </div>

      {/* Content */}

      <div
        className="
          min-w-0
          p-3
          sm:p-5
        "
      >
        {children}
      </div>

    </section>
  );
};

/* ============================================================
   FORM FIELD
============================================================ */

const FormField = ({
  label,
  icon,
  required = false,
  full = false,
  children,
}) => {
  return (
    <div
      className={`
        min-w-0
        ${full ? "md:col-span-2" : ""}
      `}
    >

      <label className="field-label flex min-w-0 items-center gap-1.5">

        {icon && (
          <span className="shrink-0 text-blue-500">
            {icon}
          </span>
        )}

        <span className="min-w-0 break-words">
          {label}
        </span>

        {required && (
          <span className="shrink-0 text-red-500">
            *
          </span>
        )}

      </label>

      {children}

    </div>
  );
};

export default RegistrationForm;
